import { AppTableName } from './Sqlite'

export type UpdateType = 'insert' | 'update' | 'delete' | 'bulk'

export interface SQLiteUpdateEvent {
  table: AppTableName
  type: UpdateType
  ids: string[] // 변경된 항목들의 localStringId (bulk일 경우 빈 배열)
  timestamp: number
  // 방금 쓴 행의 SELECT * 모양(parseRow 전). 있으면 리스너는 재조회를 생략할 수 있다.
  // updateSqliteOnly 의 소량 갱신만 싣는다 — 없으면 SQLite 에서 다시 읽을 것.
  rows?: Record<string, unknown>[]
}

// 콜백은 동기 또는 비동기(Promise) 모두 지원
type SQLiteUpdateCallback = (event: SQLiteUpdateEvent) => void | Promise<void>

export class SQLiteEventBus {
  private static instance: SQLiteEventBus
  private listeners: Map<AppTableName, Set<SQLiteUpdateCallback>> = new Map()

  private constructor() {}

  public static getInstance(): SQLiteEventBus {
    if (!SQLiteEventBus.instance) {
      SQLiteEventBus.instance = new SQLiteEventBus()
    }
    return SQLiteEventBus.instance
  }

  /**
   * 리스너 등록
   * @param table - 테이블명
   * @param callback - 이벤트 콜백 (동기 또는 비동기)
   */
  public onTableUpdated(table: AppTableName, callback: SQLiteUpdateCallback): void {
    if (!this.listeners.has(table)) {
      this.listeners.set(table, new Set())
    }
    this.listeners.get(table)!.add(callback)
  }

  /**
   * 리스너 해제
   * @param table - 테이블명
   * @param callback - 해제할 콜백
   */
  public offTableUpdated(table: AppTableName, callback: SQLiteUpdateCallback): void {
    const tableListeners = this.listeners.get(table)
    if (tableListeners) {
      tableListeners.delete(callback)
    }
  }

  /**
   * 개별/배치 업데이트 이벤트 발행 (모든 리스너 완료까지 대기)
   * @param table - 테이블명
   * @param type - 업데이트 타입 (insert, update, delete, bulk)
   * @param ids - 변경된 항목들의 localStringId 배열
   * @param rows - 방금 쓴 행(SELECT * 모양, 선택)
   * @returns 모든 리스너가 완료되면 resolve
   */
  public async emitUpdate(
    table: AppTableName,
    type: UpdateType,
    ids: string[],
    rows?: Record<string, unknown>[],
  ): Promise<void> {
    const tableListeners = this.listeners.get(table)
    if (!tableListeners || tableListeners.size === 0) return

    const event: SQLiteUpdateEvent = {
      table,
      type,
      ids,
      timestamp: Date.now(),
      rows,
    }

    // 모든 리스너를 병렬로 실행하고 완료 대기
    const promises: Promise<void>[] = []

    tableListeners.forEach((callback: SQLiteUpdateCallback) => {
      const promise = async (): Promise<void> => {
        try {
          await callback(event)
        } catch (e) {
          console.error(`[SQLiteEventBus] Error in listener for ${table}:`, e)
        }
      }
      promises.push(promise())
    })

    await Promise.all(promises)
  }

  /**
   * 대량 업데이트용 (initData, syncManager)
   * @param table - 테이블명
   * @returns 모든 리스너가 완료되면 resolve
   */
  public async emitBulkUpdate(table: AppTableName): Promise<void> {
    await this.emitUpdate(table, 'bulk', [])
  }

  /**
   * 기존 메서드 유지 (하위 호환)
   * @deprecated emitUpdate 또는 emitBulkUpdate 사용 권장
   * @param table - 테이블명
   * @returns 모든 리스너가 완료되면 resolve
   */
  public async emitTableUpdated(table: AppTableName): Promise<void> {
    await this.emitBulkUpdate(table)
  }
}
